import * as React from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import { Button, Menu, Typography } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import ProgressMenu from './progressMenu';
import MenuIcon from '../../images/hamburgerIcon.svg';
import WarningIcon from '../../images/warningIcon.svg';
import UserPanel from '../UserPanel';

import { GET_EPIC_SKILL_STATUS } from '../../apis/epic';
import { AvatarImage } from '../Avatar';

const propTypes = {
  epicSkillData: PropTypes.oneOfType([PropTypes.object]),
};

const defaultProps = {
  epicSkillData: {},
};

const SkillStatusDialog = React.memo(({ epicSkillData }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [status, setStatus] = React.useState('');
  const [dataFetched, setDataFetched] = React.useState(false);
  const params = useParams();
  const [getSkillStatus, { data }] = useLazyQuery(GET_EPIC_SKILL_STATUS, { fetchPolicy: 'no-cache' });
  const { sprint } = epicSkillData;

  const showDelay = epicSkillData.data[`${sprint}_is_delayed`];

  React.useEffect(() => {
    const fetchData = async () => {
      await getSkillStatus({
        variables: {
          input: {
            epicId: epicSkillData.data.epicId,
            projectId: parseInt(params.id, 10),
            skillId: epicSkillData.data[`${sprint}_skillId`],
          },
        },
      });
    };

    if (!dataFetched) {
      fetchData();
      setDataFetched(true);
    }
  }, [dataFetched, epicSkillData.data.epicId, params.id, sprint, getSkillStatus]);

  React.useEffect(() => {
    if (data && data.getEpicSkillStatus && data.getEpicSkillStatus.status !== null) {
      setStatus(data.getEpicSkillStatus.status);
    }
  }, [data]);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button className="menu-icon" disableRipple onClick={handleClick}>
        <img src={MenuIcon} alt="MENU-ICON" />
      </Button>
      <Menu
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            width: '280px',
            mt: '6px',
            boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.12) !important',
            borderRadius: '2px !important',
            border: '1px solid #D0D5DD',
            marginLeft: '4px',
          },
        }}
        className="assigned-member-detail"
        id="basics-menu"
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transition="true"
      >
        {showDelay && (
          <ListItem key="delay">
            <div className="delay-detail">
              <img src={WarningIcon} alt="display-icon" />
              <Typography className="display-msg">Task is delayed</Typography>
            </div>
          </ListItem>
        )}
        {epicSkillData?.data[sprint]?.map(
          (user) =>
            user[`${sprint}_is_userUnavailable`] === true && (
              <ListItem key="userUnavailable">
                <div className="delay-detail-data">
                  <img src={WarningIcon} alt="display-icon" />
                  <Typography className="display-msg-data">
                    {user[`${sprint}_displayName`]}
                    &nbsp;has a conflicting time block and may not be available
                  </Typography>
                </div>
              </ListItem>
            ),
        )}

        {epicSkillData?.data[sprint]?.map(
          (user) =>
            user[`${sprint}_is_userOverAllocated`] === true && (
              <ListItem key="userOverAllocated">
                <div className="delay-detail-data">
                  <img src={WarningIcon} alt="display-icon" />
                  <Typography className="display-msg-data">
                    {user[`${sprint}_displayName`]}
                    &nbsp;is assigned to tasks on other projects that exceed their availability
                  </Typography>
                </div>
              </ListItem>
            ),
        )}

        {epicSkillData?.data[sprint]?.map(
          (user) =>
            user[`${sprint}_userId`] !== null && (
              <ListItem key={user[`${sprint}_userId`]}>
                <ListItemAvatar>
                  <AvatarImage borderGray height={40} width={40} fontSize={20} name={user[`${sprint}_displayName`]} src={user[`${sprint}_logo`]} alt="user-logo" />
                </ListItemAvatar>
                <ListItemText>
                  <div className="user-name-title"><UserPanel user={user} sprintData={epicSkillData} /></div>
                  <Typography className="epic-skill-text">
                    {epicSkillData.data[`${sprint}_skillName`]}
                  </Typography>
                  <Typography className="email-text">{user[`${sprint}_email`]}</Typography>
                </ListItemText>
              </ListItem>
            ),
        )}

        <ListItem key="list1">
          <ListItemText>
            <Typography className="font-small">Assigned Epic</Typography>
            <Typography className="font-large">{epicSkillData.data.assignEpic}</Typography>
          </ListItemText>
        </ListItem>
        <ListItem key="list2">
          <ListItemText>
            <Typography className="font-small">Status</Typography>
            <Typography className="font-large">{status}</Typography>
          </ListItemText>
          <ProgressMenu
            status={status}
            onChange={(value) => setStatus(value)}
            epicSkillData={epicSkillData}
          />
        </ListItem>
      </Menu>
    </>
  );
});

SkillStatusDialog.propTypes = propTypes;
SkillStatusDialog.defaultProps = defaultProps;
export default SkillStatusDialog;
